import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '@hooks/useAuth.jsx';
import authApi from '@services/authApi.jsx';
import branchApi from "@services/branchApi.jsx";
import roleApi from "@services/roleApi.jsx"; // 역할 API 추가
import '@assets/css/auth/loginPendingApproval.css';

export default function LoginPendingApproval() {
    const { submitPendingApprovalInfo, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const API_BASE_URL = import.meta.env.VITE_ADMIN_BACKEND_API_URL || 'http://localhost:8080/api/v1';

    // 사용자 데이터 로딩 상태 관리
    const [userData, setUserData] = useState(null);
    const [branchList, setBranchList] = useState([]);
    const [roleList, setRoleList] = useState([]); // 역할 목록 상태 추가
    const [dataLoading, setDataLoading] = useState(true);

    // 폼 상태 관리
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        phone: '',
        userRoles: []
    });

    // 로딩 및 에러 상태 관리
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // 역할 목록 가져오기
    useEffect(() => {
        async function getRoleList() {
            try {
                const response = await roleApi.getRoles();
                
                // roleApi.getRoles()가 직접 data를 반환하는 경우와 response 객체를 반환하는 경우를 모두 처리
                const apiRoleData = response?.content || response?.data?.content || (Array.isArray(response) ? response : []);
                
                if (Array.isArray(apiRoleData) && apiRoleData.length > 0) {
                    
                    // requiresBranch 필드가 없는 경우 roleType에 따라 설정
                    const rolesWithBranchRequirement = apiRoleData.map(role => ({
                        ...role,
                        requiresBranch: role.requiresBranch !== undefined 
                            ? role.requiresBranch 
                            : role.roleType !== 'ROLE_ADMIN' // ROLE_ADMIN이 아닌 경우 모두 지점 필요
                    }));
                    
                    // Admin, Manager, User 역할 순으로 정렬
                    const sortedRoles = rolesWithBranchRequirement.sort((a, b) => {
                        // 먼저 roleType을 기준으로 정렬
                        const roleTypeOrder = {
                            "ROLE_ADMIN": 1,
                            "ROLE_MANAGER": 2,
                            "ROLE_USER": 3
                        };
                        
                        return (roleTypeOrder[a.roleType] || 99) - (roleTypeOrder[b.roleType] || 99);
                    });
                    
                    setRoleList(sortedRoles);
                } else {
                    console.warn('역할 데이터가 비어있거나 배열이 아닙니다');
                    // 폴백 설정
                    setRoleList([
                        { id: 1, name: '일반 사용자', roleType: 'ROLE_USER', requiresBranch: true },
                        { id: 2, name: '관리자', roleType: 'ROLE_MANAGER', requiresBranch: true },
                        { id: 3, name: '최고 관리자', roleType: 'ROLE_ADMIN', requiresBranch: false }
                    ]);
                }
            } catch (error) {
                console.error('역할 목록 가져오기 실패:', error);
                // 기본 역할 설정 (백업)
                setRoleList([
                    { id: 1, name: '일반 사용자', roleType: 'ROLE_USER', requiresBranch: true },
                    { id: 2, name: '관리자', roleType: 'ROLE_MANAGER', requiresBranch: true },
                    { id: 3, name: '최고 관리자', roleType: 'ROLE_ADMIN', requiresBranch: false }
                ]);
            }
        }
        getRoleList();
    }, []);

    // 브랜치 목록 가져오기
    useEffect(() => {
        async function getBranchList() {
            try {
                const branchData = await branchApi.getBranches();

                if (Array.isArray(branchData)) {
                    // 중복 제거
                    const uniqueBranches = branchData.reduce((acc, branch) => {
                        if (!acc.some((item) => item.id === branch.id)) {
                            acc.push(branch);
                        }
                        return acc;
                    }, []);
                    setBranchList(uniqueBranches);
                } else {
                    console.warn('브랜치 데이터가 배열이 아닙니다:', branchData);
                    setBranchList([]);
                }
            } catch (error) {
                console.error('브랜치 목록 가져오기 실패:', error);
                setBranchList([]);
            }
        }
        getBranchList();
    }, []);

    // 사용자 정보 가져오기
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                setDataLoading(true);
                setError(null);

                // 백엔드 API에서 사용자 정보 가져오기
                const response = await authApi.getCurrentUser();

                // API 응답 데이터 확인 및 설정
                if (response && response.data) {
                    const apiUserData = response.data;

                    // userData 상태 업데이트
                    setUserData(apiUserData);

                    const userRoles = apiUserData.userRoles || [{
                        roleId: null,
                        branchId: null
                    }];

                    // 폼 데이터 설정
                    setFormData({
                        email: apiUserData.email || '',
                        name: apiUserData.name || '',
                        phone: apiUserData.phone || '',
                        userRoles: userRoles
                    });
                } else {
                    console.warn('API 응답에 사용자 데이터가 없습니다:', response);
                    // 폴백 시도
                    tryFallbackToToken();
                }
            } catch (error) {
                console.error('API에서 사용자 정보 가져오기 실패:', error);
                setError('사용자 정보를 불러오는데 실패했습니다. ' + (error.message || ''));
                // 오류 발생 시 토큰에서라도 기본 정보 추출 시도
                tryFallbackToToken();
            } finally {
                setDataLoading(false);
            }
        };

        // 토큰에서 기본 정보 추출 시도 (API 호출 실패 시 폴백)
        const tryFallbackToToken = () => {
            try {
                const accessToken = localStorage.getItem('ab_at');
                if (!accessToken) return;

                const tokenParts = accessToken.split('.');
                if (tokenParts.length === 3) {
                    const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
                    const jsonPayload = decodeURIComponent(
                        atob(base64)
                        .split('')
                        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                        .join('')
                    );
                    const payload = JSON.parse(jsonPayload);

                    // 폼 데이터 설정 (최소한의 이메일만이라도)
                    setFormData(prev => ({
                        ...prev,
                        email: payload.sub || prev.email,
                        role: payload.roles || ['ROLE_USER']
                    }));

                }
            } catch (tokenError) {
                console.error('토큰에서 정보 추출 시도 실패:', tokenError);
            }
        };

        fetchUserData();
    }, []);

    // 입력 필드 변경 처리
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // 역할 선택 시 해당 역할이 브랜치를 필요로 하는지 확인
    const checkRoleRequiresBranch = (roleId) => {
        if (!roleId) return true; // 기본값: 브랜치 필요
        const selectedRole = roleList.find(role => role.id === Number(roleId));
        
        
        // requiresBranch가 없으면 roleType으로 판단
        if (selectedRole) {
            if (selectedRole.requiresBranch !== undefined) {
                return selectedRole.requiresBranch;
            }
            // ROLE_ADMIN 타입이면 지점 불필요
            return selectedRole.roleType !== 'ROLE_ADMIN';
        }
        
        return true; // 기본값: 브랜치 필요
    };

    // 역할-지점 추가 함수
    const addUserRole = () => {
        // 기본 역할 ID 설정 (첫 번째 사용 가능한 역할 또는 1)
        const defaultRoleId = roleList.length > 0 ? roleList[0].id : 1;
        
        setFormData(prevData => ({
            ...prevData,
            userRoles: [
                ...prevData.userRoles,
                { roleId: defaultRoleId, branchId: null }
            ]
        }));
    };

    // 역할-지점 제거 함수
    const removeUserRole = (index) => {
        setFormData(prevData => {
            const newUserRoles = [...prevData.userRoles];
            newUserRoles.splice(index, 1);
            return {
                ...prevData,
                userRoles: newUserRoles
            };
        });
    };

    // 역할-지점 수정 함수
    const updateUserRole = (index, field, value) => {
        setFormData(prevData => {
            const newUserRoles = [...prevData.userRoles];
            
            // roleId가 변경되었고, 이 역할이 브랜치를 필요로 하지 않는 경우
            if (field === 'roleId' && !checkRoleRequiresBranch(value)) {
                newUserRoles[index] = {
                    ...newUserRoles[index],
                    [field]: value,
                    branchId: null // 브랜치 ID를 null로 설정
                };
            } else {
                newUserRoles[index] = {
                    ...newUserRoles[index],
                    [field]: value
                };
            }
            
            return {
                ...prevData,
                userRoles: newUserRoles
            };
        });
    };

    // 폼 제출 처리
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setIsSubmitting(true);
            setError(null);


            // API 호출
            const response = await submitPendingApprovalInfo(formData);

            // 성공 처리
            setSuccess(true);

            // 성공 메시지 표시 후 대시보드 또는 홈 페이지로 리다이렉트
            setTimeout(() => {
                navigate('/loginWaitingApproval');
            }, 2000);

        } catch (error) {
            // 오류 처리
            console.error('추가 정보 제출 실패:', error);
            setError(error.response?.data?.message || '추가 정보 제출 중 오류가 발생했습니다. 다시 시도해 주세요.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // 데이터 로딩 중 표시
    if (dataLoading || authLoading) {
        return <div className="loading">사용자 정보를 불러오는 중입니다...</div>;
    }

    return (
        <div className="login-pending-approval">
            <h2>추가 정보 입력</h2>
            <p>계정 승인을 위해 추가 정보를 입력해 주세요.</p>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {success && (
                <div className="success-message">
                    추가 정보가 성공적으로 제출되었습니다. 관리자 승인을 기다려 주세요.
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>
                        <h2>이메일</h2>
                        <input
                            type="email"
                            value={formData.email}
                            readOnly={true}
                        />
                    </label>
                </div>

                <div className="form-group">
                    <label>
                        <h2>이름</h2>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="이름을 입력하세요"
                            required
                        />
                    </label>
                </div>

                <div className="form-group">
                    <label>
                        <h2>연락처</h2>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="연락처를 입력하세요"
                            required
                        />
                    </label>
                </div>

                <div className="form-section">
                    <h3>권한 설정</h3>
                    <p>사용자의 역할과 접근 가능한 지점을 설정하세요.</p>

                    {formData.userRoles.map((userRole, index) => (
                        <div key={index} className="user-role-item">
                            <div className="role-header">
                                <h4>권한 #{index + 1}</h4>
                                {formData.userRoles.length > 1 && (
                                    <button
                                        type="button"
                                        className="remove-button"
                                        onClick={() => removeUserRole(index)}
                                    >
                                        삭제
                                    </button>
                                )}
                            </div>

                            <div className="role-fields">
                                <div className="form-group">
                                    <label>
                                        <h2>역할</h2>
                                        <select
                                            value={userRole.roleId || ''}
                                            onChange={(e) => updateUserRole(index, 'roleId', Number(e.target.value))}
                                            required
                                        >
                                            <option value="">역할을 선택하세요</option>
                                            {roleList.map(role => (
                                                <option key={role.id} value={role.id}>
                                                    {role.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                {userRole.roleId && checkRoleRequiresBranch(userRole.roleId) && (
                                    <div className="form-group">
                                        <label>
                                            <h2>지점</h2>
                                            <select
                                                value={userRole.branchId || ''}
                                                onChange={(e) => updateUserRole(
                                                    index,
                                                    'branchId',
                                                    e.target.value ? Number(e.target.value) : null
                                                )}
                                                required
                                            >
                                                <option value="">지점을 선택하세요</option>
                                                {branchList.map(branch => (
                                                    <option key={branch.id} value={branch.id}>
                                                        {branch.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>
                                )}
                                
                                {userRole.roleId && !checkRoleRequiresBranch(userRole.roleId) && (
                                    <div className="form-group">
                                        <p className="info-text">이 역할은 지점 선택이 필요하지 않습니다. 모든 지점에 접근 가능합니다.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    <button
                        type="button"
                        className="add-role-button"
                        onClick={addUserRole}
                    >
                        + 권한 추가
                    </button>
                </div>

                
                <div className="submit-button-container">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="submit-button"
                >
                    {isSubmitting ? '처리 중...' : '저장'}
                    </button>
                </div>
            </form>
        </div>
    );
}